import apiClient from './api';

export interface OrderDetails {
  orderNumber: string;
  status: 'Pending' | 'Confirmed' | 'In Progress' | 'Delivering' | 'Completed' | 'Cancelled';
  vendor: {
    name: string;
    category: string;
    tags: string;
    image: string;
  };
  customer: {
    name: string;
    phone: string;
    email: string;
  };
  event: {
    dateTime: string;
    location: string;
  };
  dispute?: {
    message: string;
    timestamp: string;
  };
  services: Array<{
    id: string;
    name: string;
    price: string;
    duration: string;
    items: string[];
    specialRequests?: string;
    image: string;
  }>;
  total: string;
}

export interface ProductPage {
  productId: string;
  name: string;
  quantity?: number;
  price?: number;
  [key: string]: any; // Allow additional properties
}

export interface Package {
  packageId: string;
  productId?: string;
  type?: string;
  title?: string;
  subTitle?: string;
  selectedOptions?: Array<{ id: string; label: string }>;
  selectedItems?: Array<{ itemId: string; label: string; units?: number }>;
}

export interface VendorOrderDetails {
  orderId: string;
  customer: {
    userId: string;
    name: string;
    phone: string;
    email?: string;
  };
  event: {
    eventId: string;
    eventName?: string;
    currentOrderTotal?: number; // Original cart/order total before discount (from event)
    totalAmount?: number; // Event total amount (may differ from order totalAmount)
    contactDetails?: {
      contactName?: string;
      phoneNumber?: string;
    };
  } | null;
  products?: ProductPage[]; // Optional - may not be present
  productName?: string; // Single product name (new structure)
  packages?: Package[]; // Packages array (new structure)
  ordered: string | number; // createdAt
  totalAmount: number; // amount (discounted/final amount)
  currentOrderTotal?: number; // Original cart/order total before discount (may come from event or directly)
  status: string;
}

// Legacy Order interface for backward compatibility (transformed from VendorOrderDetails)
export interface Order {
  id: string;
  orderNumber: string;
  orderDate: string;
  orderTime: string;
  customerName: string;
  customerPhone: string;
  eventDate: string;
  eventTime: string;
  eventLocation: string;
  productName: string;
  productDetails: string;
  additionalProducts?: string;
  total: string; // Discounted/final amount
  originalTotal?: string; // Original price before discount (only present when discount applied)
  status: 'pending' | 'cancelled' | 'declined' | 'refund' | 'approved' | 'changing' | 'in-progress' | 'delivery' | 'completed';
}

export interface OrdersListApiResponse {
  success?: boolean;
  message?: string;
  data?: VendorOrderDetails[] | {
    orders?: VendorOrderDetails[];
    pagination?: {
      page: number;
      limit: number;
      totalPages: number;
      totalItems: number;
      hasMore: boolean;
    };
  };
  orders?: VendorOrderDetails[];
  pagination?: {
    currentPage?: number;
    page?: number;
    limit: number;
    totalPages: number;
    totalItems: number;
    hasMore: boolean;
  };
}

// Transform VendorOrderDetails to Order for display
const transformOrder = (order: VendorOrderDetails): Order => {
  // Parse ordered date (can be string or number timestamp)
  let orderedDate: Date;
  if (typeof order.ordered === 'string' && order.ordered) {
    orderedDate = new Date(order.ordered);
  } else if (typeof order.ordered === 'number' && order.ordered > 0) {
    // If it's a timestamp in seconds, convert to milliseconds
    orderedDate = new Date(order.ordered > 1000000000000 ? order.ordered : order.ordered * 1000);
  } else {
    orderedDate = new Date();
  }
  
  // Format event date (eventName might contain date info, or we use eventId)
  // Handle null event gracefully
  const eventDate = order.event?.eventName || order.event?.eventId || 'N/A';
  const eventTime = ''; // Event time not available in VendorOrderDetails
  
  // Handle customer data - check multiple sources:
  // 1. order.customer (direct customer data)
  // 2. event.contactDetails (customer data from event)
  // 3. Fallback to 'Unknown Customer' / 'N/A'
  
  // Helper function to check if a string is valid (not empty, not 'Unknown', not just whitespace)
  const isValidString = (str: string | null | undefined): boolean => {
    return !!(str && typeof str === 'string' && str.trim() && str.trim() !== 'Unknown');
  };
  
  const customerName = 
    isValidString(order.customer?.name)
      ? order.customer!.name.trim()
      : (order.event && isValidString(order.event.contactDetails?.contactName))
        ? order.event.contactDetails!.contactName!.trim()
        : 'Unknown Customer';
  
  const customerPhone = 
    isValidString(order.customer?.phone)
      ? order.customer!.phone.trim()
      : (order.event && isValidString(order.event.contactDetails?.phoneNumber))
        ? order.event.contactDetails!.phoneNumber!.trim()
        : 'N/A';
  
  // Debug logging for customer data extraction
  if (customerName === 'Unknown Customer' || customerPhone === 'N/A') {
    console.warn('⚠️ Customer data not found for order:', {
      orderId: order.orderId,
      customer: order.customer,
      eventContactDetails: order.event?.contactDetails,
      extractedName: customerName,
      extractedPhone: customerPhone,
    });
  }
  
  // Format products into product details
  // Handle productName, packages, and products array structures
  const productName = order.productName || 'No products';
  let productDetails = '';
  let additionalProducts: string | undefined = undefined;

  if (order.packages && order.packages.length > 0) {
    const packages = order.packages.filter(p => p && p.packageId);
    if (packages.length > 0) {
      
      // Build product details from package titles
      const packageTitles = packages
        .slice(0, 3)
        .map(p => p.title || p.packageId || 'Package')
        .filter(Boolean);
      
      productDetails = packageTitles.join(', ');
      const remainingPackages = packages.length - 3;
      if (remainingPackages > 0) {
        productDetails = `${productDetails}, ...`;
      }
      
      additionalProducts = packages.length > 1 
        ? `+${packages.length - 1} package${packages.length > 2 ? 's' : ''}` 
        : undefined;
    } else {
      productDetails = 'No packages in this order';
    }
  }
  
  // Normalize status to match Order interface
  const normalizeStatus = (status: string): Order['status'] => {
    if (!status || !status.trim()) return 'pending';
    const lowerStatus = status.toLowerCase().trim();
    
    // Exact matches first (case-insensitive)
    if (lowerStatus === 'pending') return 'pending';
    if (lowerStatus === 'cancelled' || lowerStatus === 'canceled') return 'cancelled';
    if (lowerStatus === 'declined') return 'declined';
    if (lowerStatus === 'refund' || lowerStatus === 'refunded') return 'refund';
    if (lowerStatus === 'approved') return 'approved';
    if (lowerStatus === 'changing') return 'changing';
    if (lowerStatus === 'in-progress' || lowerStatus === 'in progress') return 'in-progress';
    if (lowerStatus === 'delivery' || lowerStatus === 'delivering') return 'delivery';
    if (lowerStatus === 'completed' || lowerStatus === 'complete') return 'completed';
    
    return 'pending'; // Default
  };
  
  // Calculate original total if currentOrderTotal is available and different from totalAmount
  // Check both order-level currentOrderTotal and event-level currentOrderTotal
  const finalAmount = order.totalAmount || 0;
  const originalAmount = order.currentOrderTotal || order.event?.currentOrderTotal;
  const hasDiscount = originalAmount && originalAmount > finalAmount;
  
  return {
    id: order.orderId || 'N/A',
    orderNumber: order.orderId || 'N/A',
    orderDate: orderedDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
    orderTime: orderedDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
    customerName,
    customerPhone,
    eventDate,
    eventTime,
    eventLocation: '',
    productName,
    productDetails,
    additionalProducts,
    total: `QAR ${finalAmount.toFixed(2)}`,
    originalTotal: hasDiscount ? `QAR ${originalAmount.toFixed(2)}` : undefined,
    status: normalizeStatus(order.status || 'pending'),
  };
}

export interface OrdersApiResponse {
  success?: boolean;
  message?: string;
  data?: OrderDetails;
  order?: OrderDetails;
}

export const vendorOrdersService = {
  // Get list of orders for a vendor
  getVendorOrders: async (
    vendorId: string,
    page: number = 1,
    limit: number = 20,
    status?: string
  ): Promise<{ orders: Order[]; pagination?: OrdersListApiResponse['pagination'] }> => {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (status && status !== 'Any status') {
        params.append('status', status);
      }
      
      const url = `/vendors/${vendorId}/orders/details?${params.toString()}`;
      console.log('📦 Fetching vendor orders from:', url);
      
      const response = await apiClient.get<OrdersListApiResponse>(url);
      const responseData = response.data;
      
      // Handle different response structures
      let vendorOrders: VendorOrderDetails[] = [];
      let pagination: OrdersListApiResponse['pagination'] = undefined;
      
      // Check for { success: true, data: [...], pagination: {...} } structure first
      if (responseData.success && Array.isArray(responseData.data)) {
        vendorOrders = responseData.data as VendorOrderDetails[];
        // Pagination is at root level when data is an array
        if (responseData.pagination) {
          pagination = {
            page: responseData.pagination.currentPage || responseData.pagination.page || 1,
            limit: responseData.pagination.limit || limit,
            totalPages: responseData.pagination.totalPages || 0,
            totalItems: responseData.pagination.totalItems || 0,
            hasMore: responseData.pagination.hasMore || false,
          };
          console.log('📄 Extracted pagination:', pagination);
        }
      } else if (responseData.data && typeof responseData.data === 'object' && !Array.isArray(responseData.data) && responseData.data.orders) {
        vendorOrders = responseData.data.orders;
        pagination = responseData.data.pagination;
      } else if (responseData.orders) {
        vendorOrders = responseData.orders;
        if (responseData.pagination) {
          pagination = {
            page: responseData.pagination.currentPage || responseData.pagination.page || 1,
            limit: responseData.pagination.limit || limit,
            totalPages: responseData.pagination.totalPages || 0,
            totalItems: responseData.pagination.totalItems || 0,
            hasMore: responseData.pagination.hasMore || false,
          };
        }
      } else if (Array.isArray(responseData)) {
        vendorOrders = responseData as unknown as VendorOrderDetails[];
      } else if (responseData.data && Array.isArray(responseData.data)) {
        vendorOrders = responseData.data as unknown as VendorOrderDetails[];
        if (responseData.pagination) {
          pagination = {
            page: responseData.pagination.currentPage || responseData.pagination.page || 1,
            limit: responseData.pagination.limit || limit,
            totalPages: responseData.pagination.totalPages || 0,
            totalItems: responseData.pagination.totalItems || 0,
            hasMore: responseData.pagination.hasMore || false,
          };
        }
      }
      
      console.log('📦 Extracted orders:', vendorOrders.length);
      console.log('📄 Extracted pagination:', pagination);
      
      // Log raw data to help debug null/empty values
      if (vendorOrders.length > 0) {
        const sampleOrder = vendorOrders[0];
        console.log('📦 Raw order data sample:', {
          orderId: sampleOrder.orderId,
          customer: sampleOrder.customer,
          customerName: sampleOrder.customer?.name,
          customerPhone: sampleOrder.customer?.phone,
          event: sampleOrder.event,
          eventContactDetails: sampleOrder.event?.contactDetails,
          productName: sampleOrder.productName,
          packages: sampleOrder.packages,
          packagesLength: sampleOrder.packages?.length || 0,
          products: sampleOrder.products,
          productsLength: sampleOrder.products?.length || 0,
          ordered: sampleOrder.ordered,
          totalAmount: sampleOrder.totalAmount,
          currentOrderTotal: sampleOrder.currentOrderTotal,
          status: sampleOrder.status,
        });
      }
      
      // Sort by ordered date descending (newest first) before transforming
      vendorOrders.sort((a, b) => {
        const dateA = typeof a.ordered === 'number' ? a.ordered : new Date(a.ordered || 0).getTime();
        const dateB = typeof b.ordered === 'number' ? b.ordered : new Date(b.ordered || 0).getTime();
        return dateB - dateA;
      });

      // Transform VendorOrderDetails to Order for display
      const orders = vendorOrders.map(transformOrder);
      
      return { orders, pagination };
    } catch (error: any) {
      console.error('❌ Error fetching vendor orders:', {
        error,
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data
      });
      throw error;
    }
  },
  
  // Get order details for a vendor
  getOrderDetails: async (vendorId: string, orderId?: string): Promise<OrderDetails> => {
    try {
      const url = `/vendors/${vendorId}/orders/details${orderId ? `?orderId=${orderId}` : ''}`;
      console.log('📦 Fetching order details from:', url);
      
      const response = await apiClient.get<OrdersApiResponse>(url);
      const responseData = response.data;
      
      // Handle different response structures
      if (responseData.data) {
        return responseData.data;
      }
      if (responseData.order) {
        return responseData.order;
      }
      if (responseData.success && responseData.data) {
        return responseData.data;
      }
      
      // If response is directly the order details
      return responseData as unknown as OrderDetails;
    } catch (error: any) {
      console.error('❌ Error fetching order details:', {
        error,
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data
      });
      throw error;
    }
  },
};

export default vendorOrdersService;


